import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import JourneyMap from "@/components/journey/JourneyMap";
import HowItWorks from "@/components/HowItWorks";
import HeroPreview from "@/components/HeroPreview";
import SkylineBackdrop from "@/components/SkylineBackdrop";
import FragmentReveal from "@/components/FragmentReveal";
import Reveal from "@/components/Reveal";
import { track } from "@/lib/analytics";
import {
  ArrowRight, Lock, BadgeDollarSign, ClipboardCheck,
  MessageSquare, Calendar, Calculator, FileText, Scale,
  Search, FileSignature, Coins, Sparkles, Quote,
} from "lucide-react";

const BLUE = "#1d4ed8";
const BLUE_SOFT = "#eff6ff";
const BLUE_BORDER = "#bfdbfe";

const TOOLS = [
  { num: "01", to: "/chat",          title: "AI Chat",              blurb: "Ask anything. Get real answers.",                        Icon: MessageSquare },
  { num: "02", to: "/deadlines",     title: "Deadline Tracker",     blurb: "Your state's deadline and what's at stake.",             Icon: Calendar },
  { num: "03", to: "/estimator",     title: "SAI Estimator",        blurb: "What your numbers mean in plain English.",               Icon: Calculator },
  { num: "04", to: "/decoder",       title: "Aid Letter Decoder",   blurb: "What your offer letter actually says.",                  Icon: FileText },
  { num: "05", to: "/compare",       title: "School Comparer",      blurb: "True cost side-by-side for up to 4 schools.",            Icon: Scale },
  { num: "06", to: "/scholarships",  title: "Scholarship Finder",   blurb: "Real scholarships with real odds.",                      Icon: Search },
  { num: "07", to: "/appeal",        title: "Appeal Writer",        blurb: "A professional appeal in 2 minutes.",                    Icon: FileSignature },
  { num: "08", to: "/loans",         title: "Loan Reality Check",   blurb: "What a loan costs before you sign.",                     Icon: Coins },
  { num: "09", to: "/netprice",      title: "Net Price Calculator", blurb: "What a school will really cost, in 60 seconds.",         Icon: Sparkles },
  { num: "10", to: "/find",          title: "Find Schools (AI)",    blurb: "Grade your dream schools and find 5 hidden gems most people miss.", Icon: Search },
];

const QA = [
  {
    q: "My SAI is 8,400. What does that mean?",
    a: "It means your family is expected to contribute about $8,400/year. You likely won't qualify for a Pell Grant, but many schools offer institutional grants for families at this level. The real question is each school's net price, that varies far more than your SAI.",
  },
  {
    q: "Vanderbilt offered $61K in grants. Is that good?",
    a: "Yes if your COA is $82K, your net cost is $21K/year, well below their sticker price. The key is how much of that $61K is grants vs. loans. If it's all grants, that's genuinely strong aid.",
  },
  {
    q: "Tennessee's deadline passed. Can I still get state aid?",
    a: "For TN's state grants, unfortunately no, the December 1 deadline is hard. But federal aid (Pell, loans) runs on the federal deadline, and your school's own institutional aid may have separate deadlines. File today anyway.",
  },
];

const FOOTER_TOOLS = [
  { to: "/chat",         label: "Chat" },
  { to: "/deadlines",    label: "Deadlines" },
  { to: "/estimator",    label: "Estimator" },
  { to: "/scholarships", label: "Scholarships" },
  { to: "/decoder",      label: "Aid Decoder" },
  { to: "/compare",      label: "Compare" },
  { to: "/appeal",       label: "Appeal" },
  { to: "/loans",        label: "Loans" },
  { to: "/netprice",     label: "Net Price" },
  { to: "/journey",      label: "The Journey" },
  { to: "/sidebyside",   label: "Side-by-Side" },
  { to: "/glossary",     label: "Glossary" },
];

export default function LandingPage() {
  const [qaIdx, setQaIdx] = useState(0);
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setQaIdx(i => (i + 1) % QA.length), 6500);
    return () => clearInterval(t);
  }, []);

  // Mark the body so the global heading-italic-suppression CSS allows
  // the homepage hero accent to remain italic. Cleared on unmount so
  // every other route inherits the upright-roman default.
  useEffect(() => {
    document.body.setAttribute("data-route", "home");
    return () => { document.body.removeAttribute("data-route"); };
  }, []);

  const onSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmittedEmail(true);
    setTimeout(() => {
      window.location.href = `/deadlines?email=${encodeURIComponent(email)}`;
    }, 250);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900">
      {/* ANNOUNCEMENT BAR */}
      <div className="announce-bar flex items-center justify-center px-4" data-testid="announce-bar">
        <span className="truncate">
          FAFSA 2025-2026 is now open
          <span className="mx-2 ann-sep">·</span>
          <Link to="/deadlines" className="hover:underline underline-offset-2" data-testid="announce-link">
            Check your state's deadline →
          </Link>
        </span>
      </div>

      <Nav />

      {/* HERO — asymmetric editorial */}
      <section className="relative bg-white overflow-hidden aw-hero-section">
        <SkylineBackdrop which="princeton" />
        <FragmentReveal cols={72} rows={30} duration={0.55} spreadDelay={1.1} jitter={0.18} startOffset={-0.15} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pt-10 sm:pb-20 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center" data-testid="hero">
          <div className="lg:col-span-7">
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-bold border mb-7 aw-rise"
              style={{ borderColor: "#1d4ed8", background: BLUE_SOFT, color: "#1e3a8a", "--aw-delay": "0.45s" }}
              data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#15803d" }} />
              College financial aid, made easy
            </span>

            <h1
              className="font-heading text-gray-900 hero-italic-allowed aw-rise"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", lineHeight: 1.04, letterSpacing: "-0.015em", "--aw-delay": "0.55s" }}>
              College costs less<br />
              when you know how{" "}
              <span className="whitespace-nowrap">
                the <em className="italic" style={{ color: BLUE }}>money works.</em>
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg max-w-xl leading-relaxed text-gray-700 aw-rise" style={{ "--aw-delay": "0.7s" }}>
              Free tools and real answers for every step of the financial aid process,
              from your first question to comparing offers from multiple schools.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center aw-rise" style={{ "--aw-delay": "0.85s" }}>
              <Link
                to="/chat"
                onClick={() => track.landing_cta_click()}
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
                style={{ background: BLUE, color: "#ffffff" }}>
                Ask the AI <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#tools"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                See all tools
              </a>
            </div>

          </div>

          <div className="lg:col-span-5 aw-card-in" style={{ "--aw-delay": "1.0s" }}>
            <div className="flex justify-end mb-6">
              <a
                href="https://www.producthunt.com/products/aidwaive?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-aidwaive"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-opacity hover:opacity-90"
                data-testid="producthunt-badge">
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1143783&theme=light&t=1778445771118"
                  alt="AidWaive - Automating the financial aid process | Product Hunt"
                  width="210"
                  height="45"
                  style={{ width: 210, height: 45 }}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </div>
            <HeroPreview />
          </div>
        </div>
      </section>

      {/* JOURNEY MAP */}
      <section className="relative border-t border-gray-200 bg-gray-50 overflow-hidden">
        <SkylineBackdrop which="stanford" />
        <Reveal className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="mb-10 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">The journey</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
              Your year, <em className="italic" style={{ color: BLUE }}>at a glance.</em>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-500">
              Every step from creating your FSA ID to commitment day. Tap any milestone for context and the right tool to use.
            </p>
          </div>

          <Card>
            <div className="p-5 sm:p-6">
              <JourneyMap />
            </div>
          </Card>

          <div className="mt-7 flex justify-end">
            <Link to="/journey" data-testid="landing-journey-cta"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium bg-white shadow-sm hover:shadow-md transition-shadow"
              style={{ color: "#1e3a8a", border: "1px solid #bfdbfe" }}>
              See the full journey, step by step <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* TOOL GRID — premium cards */}
      <section id="tools" className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <Reveal className="mb-12 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">Nine free tools</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
              No account <em className="italic" style={{ color: BLUE }}>needed.</em>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-500">
              Each tool does one thing well. Pick the one you need right now.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {TOOLS.map((t, i) => (
              <Reveal key={t.to} delay={i * 60}>
                <ToolCard {...t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <Reveal><HowItWorks /></Reveal>

      {/* STATS — editorial */}
      <section className="bg-gray-50 border-b border-gray-200" data-testid="stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Reveal className="mb-12 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">Why this matters</div>
            <h2 className="font-heading text-3xl sm:text-4xl leading-[1.1] text-gray-900">
              The aid gap, <em className="italic" style={{ color: BLUE }}>in numbers.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Reveal delay={0}><Stat n="$3.7B"   label="Pell grants unclaimed every year" /></Reveal>
            <Reveal delay={90}><Stat n="40%"     label="Of eligible students never file" /></Reveal>
            <Reveal delay={180}><Stat n="20M+"    label="Families file FAFSA annually" /></Reveal>
            <Reveal delay={270}><Stat n="$200/hr" label="What a financial aid advisor costs" /></Reveal>
          </div>
        </div>
      </section>

      {/* REAL ANSWERS, Q&A demo */}
      <section className="bg-white border-b border-gray-200">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">Real answers</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
              Not <em className="italic" style={{ color: BLUE }}>"consult a professional."</em>
            </h2>
            <p className="mt-4 leading-relaxed text-gray-500">
              Specific, dollar-aware, no hedging. Here's what AidWaive actually says.
            </p>

            <div className="mt-8 flex items-center gap-2" data-testid="qa-indicator">
              {QA.map((_, i) => (
                <button key={i} onClick={() => setQaIdx(i)}
                  aria-label={`Show example ${i + 1}`}
                  data-testid={`qa-dot-${i}`}
                  className={`h-1 rounded-full transition-all ${i === qaIdx ? "w-10" : "w-5"}`}
                  style={{ background: i === qaIdx ? BLUE : "#e5e7eb" }} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: "#22c55e" }} />
                <span className="text-xs uppercase tracking-[0.18em] text-gray-400">AidWaive AI</span>
              </div>
              <div className="p-5 sm:p-7 space-y-5">
                <div className="flex justify-end fade-in" key={`q-${qaIdx}`}>
                  <div className="max-w-[88%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm text-gray-900"
                    style={{ background: "#f3f4f6" }}>
                    {QA[qaIdx].q}
                  </div>
                </div>
                <div className="flex fade-in" key={`a-${qaIdx}`}>
                  <div className="max-w-[92%] rounded-2xl rounded-bl-sm px-4 py-3 text-[15px] leading-relaxed text-gray-900"
                    style={{ background: BLUE_SOFT, border: `1px solid ${BLUE_BORDER}` }}>
                    {QA[qaIdx].a}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </Reveal>
      </section>

      {/* TRUST */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Reveal className="mb-12 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">What you're getting</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
              Free, <em className="italic" style={{ color: BLUE }}>actually.</em>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            <Reveal delay={0}><Trust icon={Lock}            title="Nothing stored."  copy="All calculations run in your browser. We never see your numbers." /></Reveal>
            <Reveal delay={100}><Trust icon={BadgeDollarSign} title="Actually free."   copy="No account. No trial. No paywall. Nine tools, zero cost." /></Reveal>
            <Reveal delay={200}><Trust icon={ClipboardCheck}  title="Real answers."    copy="Not 'consult a professional.' Actual information you can act on." /></Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL — editorial */}
      <section className="bg-white border-b border-gray-200">
        <Reveal className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Quote className="w-8 h-8 mb-7" strokeWidth={1.4} style={{ color: BLUE }} />
          <blockquote
            className="font-heading leading-[1.18] text-gray-900 max-w-3xl"
            style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.6rem)" }}
            data-testid="testimonial-quote">
            <em className="italic" style={{ color: BLUE }}>AidWaive told us</em> our state deadline was two months before the federal one. We almost lost $4,200 in grants because nobody told us.
          </blockquote>
          <div className="mt-10 flex items-center gap-3" data-testid="testimonial-attribution">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium"
              style={{ background: BLUE_SOFT, color: BLUE, border: `1px solid ${BLUE_BORDER}` }}>
              BU
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Beta user</div>
              <div className="text-xs text-gray-500">Tennessee · saved $4,200 in grants</div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative bg-gray-50 overflow-hidden flex items-center">
        <SkylineBackdrop which="umich" />
        <Reveal className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full" data-testid="bottom-cta">
          <Card>
            <div className="p-10 sm:p-14 text-center">
              <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] text-gray-900">
                Start now. <em className="italic" style={{ color: BLUE }}>No account needed.</em>
              </h2>
              <p className="mt-5 max-w-md mx-auto leading-relaxed text-gray-500">
                Pick the one tool that solves your problem today. Come back tomorrow if you need another.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
                <Link to="/chat" data-testid="bottom-cta-chat"
                  className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
                  style={{ background: BLUE, color: "#ffffff" }}>
                  Ask the AI <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/deadlines" data-testid="bottom-cta-deadlines"
                  className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                  Check my deadline
                </Link>
              </div>
            </div>
          </Card>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="bg-white text-gray-500 border-t border-gray-200" data-testid="footer">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <Link to="/" className="inline-block mb-3" data-testid="footer-logo">
                <img
                  src="/aidwaive-logo.png"
                  alt="AidWaive"
                  className="h-10 sm:h-11 w-auto object-contain select-none"
                  draggable={false}
                />
              </Link>
              <p className="text-sm leading-relaxed max-w-xs">
                Free financial-aid help for American families. No login. Nothing stored.
              </p>
              <p className="text-xs mt-6 text-gray-400">
                © {new Date().getFullYear()} AidWaive. Not affiliated with the U.S. Department of Education.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] mb-4 text-gray-400">Tools</div>
                <ul className="space-y-2">
                  {FOOTER_TOOLS.map(t => (
                    <li key={t.to}>
                      <Link to={t.to} className="text-sm hover:text-gray-900 transition-colors"
                        data-testid={`footer-tool-${t.to.slice(1)}`}>
                        {t.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] mb-4 text-gray-400">Legal</div>
                <ul className="space-y-2">
                  <li><Link to="/legal/privacy" className="text-sm hover:text-gray-900 transition-colors" data-testid="footer-privacy">Privacy</Link></li>
                  <li><Link to="/legal/terms"   className="text-sm hover:text-gray-900 transition-colors" data-testid="footer-terms">Terms</Link></li>
                  <li>
                    <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer"
                      className="text-sm hover:text-gray-900 transition-colors" data-testid="footer-studentaid">
                      studentaid.gov ↗
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] mb-4 text-gray-400">Get deadline alerts</div>
              <p className="text-sm leading-relaxed mb-4">
                The only email AidWaive will ever send: deadline reminders for your state.
              </p>
              <form onSubmit={onSubscribe} className="flex gap-2" data-testid="footer-newsletter-form">
                <input
                  type="email" required placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  disabled={submittedEmail}
                  className="flex-1 h-11 px-3 rounded-md text-sm outline-none"
                  style={{ background: "#ffffff", border: "1px solid #e5e7eb", color: "#111928" }}
                  data-testid="footer-newsletter-email" />
                <button type="submit" disabled={submittedEmail || !email}
                  className="px-4 h-11 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: BLUE, color: "#ffffff" }}
                  data-testid="footer-newsletter-submit">
                  {submittedEmail ? "Opening" : "Subscribe"}
                </button>
              </form>
              <p className="text-[11px] mt-3 text-gray-400">Double opt-in. One-click unsubscribe.</p>
            </div>
          </div>
        </Reveal>
      </footer>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Reusable building blocks                                              */
/* ──────────────────────────────────────────────────────────────────── */

function Card({ children }) {
  return (
    <div
      className="rounded-2xl bg-white transition-all"
      style={{
        border: "1px solid #bfdbfe",
        boxShadow: "0 1px 2px rgba(30,58,138,0.06)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 12px 32px rgba(30,58,138,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(30,58,138,0.06)"; }}>
      {children}
    </div>
  );
}

function ToolCard({ num, to, title, blurb, Icon }) {
  return (
    <Link
      to={to}
      onClick={() => track.tool_opened(to.slice(1))}
      data-testid={`tool-card-${to.slice(1)}`}
      className="group block rounded-2xl bg-white p-6 transition-all"
      style={{
        border: "1px solid #bfdbfe",
        boxShadow: "0 1px 2px rgba(30,58,138,0.06)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 14px 36px rgba(30,58,138,0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "#93c5fd";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(30,58,138,0.06)";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "#bfdbfe";
      }}>
      <div className="flex items-center justify-between mb-7">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: BLUE_SOFT, border: `1px solid ${BLUE_BORDER}` }}>
          <Icon className="w-4 h-4" strokeWidth={1.7} style={{ color: BLUE }} />
        </div>
        <div className="text-xs tabular-nums tracking-[0.18em] text-gray-300 font-medium">{num}</div>
      </div>
      <div className="font-sans font-medium text-[17px] mb-2 text-gray-900">{title}</div>
      <p className="text-sm leading-relaxed text-gray-600">{blurb}</p>
      <div className="mt-6 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-medium"
        style={{ color: BLUE }}>
        Open <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function Stat({ n, label }) {
  return (
    <div
      className="rounded-2xl bg-white p-7 transition-all"
      style={{ border: "1px solid #bfdbfe", boxShadow: "0 1px 2px rgba(30,58,138,0.06)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 12px 32px rgba(30,58,138,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(30,58,138,0.06)"; }}>
      <div className="font-heading text-4xl sm:text-5xl leading-none text-gray-900 tabular-nums">{n}</div>
      <div className="text-[11px] mt-4 uppercase tracking-[0.16em] text-gray-500 font-medium">{label}</div>
    </div>
  );
}

function Trust({ icon: Icon, title, copy }) {
  return (
    <div
      className="rounded-2xl p-7 bg-white transition-all"
      style={{ border: "1px solid #bfdbfe", boxShadow: "0 1px 2px rgba(30,58,138,0.06)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 12px 32px rgba(30,58,138,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(30,58,138,0.06)"; }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
        style={{ background: BLUE_SOFT, border: `1px solid ${BLUE_BORDER}` }}>
        <Icon className="w-4 h-4" strokeWidth={1.7} style={{ color: BLUE }} />
      </div>
      <div className="font-sans font-medium text-lg mb-2 text-gray-900">{title}</div>
      <p className="text-sm leading-relaxed text-gray-500">{copy}</p>
    </div>
  );
}
